import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as FileUploadIcon } from 'assets/icons/fileUpload.svg';
import { ReactComponent as TaskArrowIcon } from 'assets/icons/taskArrow.svg';
import ActivityHeader from 'components/ActivityHeader/ActivityHeader';
import EmptyList from 'components/EmptyList/EmptyList';
import { COMMENTS_TYPES } from 'constants/general';
import { useLazyGetIssignUsersInfoQuery } from 'store/slices/tasksManagement/apis/tasksManagementApi';
import { useLazyGetValueChainMapDetailsQuery } from 'store/slices/valueChainMap/apis/valueChainMapApi';
import { getPriorityIcon } from 'utils/taskHelper';

import TaskInfoActivityDateItem from './components/TaskInfoActivityDateItem/TaskInfoActivityDateItem';
import TaskInfoActivityItem from './components/TaskInfoActivityItem/TaskInfoActivityItem';

import type IDataItem from 'store/slices/tasksManagement/interfaces/IDataItem';
import type IDataItemArray from 'store/slices/tasksManagement/interfaces/IDataItemArray';
import type IDataUserAssigned from 'store/slices/tasksManagement/interfaces/IDataUserAssigned';
import type { IGetValueChainMapDetailsResponse } from 'store/slices/valueChainMap/interfaces/IGetValueChainMapDetailsResponse';
import type { ITaskInfoActivities } from './interfaces/ITaskInfoActivities';

import styles from './sass/TaskInfoActivities.module.scss';

function TaskInfoActivities({
  activities,
  getUserInitials,
  emptyListText,
}: ITaskInfoActivities) {
  const { t } = useTranslation();
  const [getIssignUsersInfo, { data: usersInfo }] = useLazyGetIssignUsersInfoQuery();
  const [getValueChainMapDetails] = useLazyGetValueChainMapDetailsQuery();
  const [VCMInfo, setVCMInfo] = useState<IGetValueChainMapDetailsResponse[]>([]);
  const checkType = (type: string) => {
    switch (type) {
      case COMMENTS_TYPES.ATTACHMENT_REMOVED:
        return true;
      case COMMENTS_TYPES.ATTACHMENT_ADDED:
        return true;
      default:
        return false;
    }
  };

  useEffect(() => {
    if (activities && activities.length > 0) {
      const userIds = activities.reduce((acc: number[], activity) => {
        if (!activity) return acc;
        const data = activity.data as IDataUserAssigned;
        if (data?.added?.length > 0 || data?.removed?.length > 0) {
          return [...acc, ...(data.added || []), ...(data.removed || [])];
        }
        return acc;
      }, []);

      if (userIds.length > 0) {
        getIssignUsersInfo({ userIds: Array.from(new Set(userIds)) });
      }
    }
  }, [activities, getIssignUsersInfo]);

  useEffect(() => {
    if (activities && activities.length > 0) {
      const valueChainMapIds = activities.reduce((acc: number[], activity) => {
        if (!activity) return acc;
        const data = activity.data as IDataItem;
        if (data?.before?.valueChainMapId && data?.after?.valueChainMapId) {
          return [...acc, data.before.valueChainMapId, data.after.valueChainMapId];
        }
        return acc;
      }, []);
      if (valueChainMapIds.length > 0) {
        valueChainMapIds?.forEach((id) => {
          if (id) {
            getValueChainMapDetails({ chainId: id.toString() }).then((response) => {
              const responseData = response.data?.data;
              if (responseData) {
                setVCMInfo((prev) => {
                  const exists = prev.some((item) => item.id === responseData.id);
                  if (exists) return prev;
                  return [...prev, responseData];
                });
              }
            });
          }
        });
      }
    }
  }, [activities, getValueChainMapDetails]);

  return (
    <div className={styles.activitiesTab}>
      <div className={styles.activitiesTimeline}>
        { activities && activities?.length > 0 ? activities?.map((activity) => (
          <div className={styles.activityItemWrapper} key={[activity.id, activity.createdAt].join('_')}>
            <div className={styles.activityItem}>
              <div className={styles.activityIconWrapper}>
                <div className={styles.activityAvatar}>
                  {getUserInitials(`${activity.user.firstName} ${activity.user.lastName}`)}
                </div>
              </div>
              <div className={styles.activityContent}>
                <ActivityHeader
                  firstName={activity.user.firstName}
                  lastName={activity.user.lastName}
                  activityType={checkType(activity.type) ? activity.type : t('taskManagement.taskChangedTo')}
                  createdAt={activity.createdAt}
                />
                <div className={styles.contentActivity}>
                  {(activity.data as IDataItem)?.before?.title ? (
                    <TaskInfoActivityItem
                      title={t('taskManagement.taskName')}
                      beforeValue={(activity.data as IDataItem)?.before?.title ?? ''}
                      afterValue={(activity.data as IDataItem)?.after?.title ?? ''}
                    />
                  ) : null}
                  {(activity.data as IDataItem)?.before?.valueChainMapId ? (
                    <TaskInfoActivityItem
                      title={t('taskManagement.relatedTo')}
                      beforeValue={VCMInfo.find(
                        (item) => item.id === (activity.data as IDataItem)?.before?.valueChainMapId,
                      )?.name ?? ''}
                      afterValue={VCMInfo.find(
                        (item) => item.id === (activity.data as IDataItem)?.after?.valueChainMapId,
                      )?.name ?? ''}
                      disableComparison
                    />
                  ) : null}
                  {(activity?.data as IDataUserAssigned)?.added
                    && (activity?.data as IDataUserAssigned)?.added?.length > 0 ? (
                      <div className={styles.contentUserAssigned}>
                        <span className={styles.titleBlock}>{t('taskManagement.userAssigned')}</span>
                        <div className={styles.userAssignedList}>
                          {(activity?.data as IDataUserAssigned)?.added.map((item, index) => (
                            <span className={styles.userName} key={[item, index].join('_')}>
                              {usersInfo?.data.find(
                                (user) => user.id === item,
                              )?.firstName}
                              {' '}
                              {usersInfo?.data.find(
                                (user) => user.id === item,
                              )?.lastName}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  {(activity?.data as IDataUserAssigned)?.removed
                    && (activity?.data as IDataUserAssigned)?.removed?.length > 0 ? (
                      <div className={styles.contentUserAssigned}>
                        <span className={styles.titleBlock}>{t('taskManagement.userRemoved')}</span>
                        <div className={styles.userAssignedList}>
                          {(activity?.data as IDataUserAssigned)?.removed.map((item, index) => (
                            <span className={styles.userName} key={[item, index].join('_')}>
                              {usersInfo?.data.find(
                                (user) => user.id === item,
                              )?.firstName}
                              {' '}
                              {usersInfo?.data.find(
                                (user) => user.id === item,
                              )?.lastName}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}

                  {(activity.data as IDataItem)?.before?.status ? (
                    <div className={styles.contentStatus}>
                      <div className={`${styles.statusBadge} 
                    ${styles[`${(activity.data as IDataItem)?.before?.status}`]}`}
                      >
                        {(activity.data as IDataItem)?.before?.status}
                      </div>
                      <TaskArrowIcon />
                      <div className={`${styles.statusBadge} 
                        ${styles[`${(activity.data as IDataItem)?.after?.status}`]}`}
                      >
                        {(activity.data as IDataItem)?.after?.status}
                      </div>
                    </div>
                  ) : null}

                  {(activity.data as IDataItem)?.before?.startDate ? (
                    <TaskInfoActivityDateItem
                      title={t('taskManagement.startDate')}
                      beforeDate={(activity.data as IDataItem)?.before?.startDate ?? ''}
                      afterDate={(activity.data as IDataItem)?.after?.startDate ?? ''}
                    />
                  ) : null}

                  {(activity.data as IDataItem)?.before?.endDate ? (
                    <TaskInfoActivityDateItem
                      title={t('taskManagement.endDate')}
                      beforeDate={(activity.data as IDataItem)?.before?.endDate ?? ''}
                      afterDate={(activity.data as IDataItem)?.after?.endDate ?? ''}
                    />
                  ) : null}

                  {(activity.data as IDataItem)?.before?.priority ? (
                    <div className={styles.itemContainer}>
                      <span className={styles.itemContainerTitle}>
                        {t('taskManagement.priority')}
                      </span>
                      <div className={styles.itemContainerValue}>
                        <span className={styles.textValue}>
                          {getPriorityIcon((activity.data as IDataItem)?.before?.priority || '')}
                          {' '}
                          {`${(activity.data as IDataItem)?.before?.priority}`}
                        </span>
                        <TaskArrowIcon />
                        <span className={styles.textValue}>
                          {getPriorityIcon((activity.data as IDataItem)?.after?.priority || '')}
                          {' '}
                          {`${(activity.data as IDataItem)?.after?.priority}`}
                        </span>
                      </div>
                    </div>
                  ) : null}

                  {(activity.data as IDataItem)?.before?.description
                  || (activity.data as IDataItem)?.after?.description ? (
                    <TaskInfoActivityItem
                      title={t('taskManagement.description')}
                      beforeValue={(activity.data as IDataItem)?.before?.description ?? ''}
                      afterValue={(activity.data as IDataItem)?.after?.description ?? ''}
                    />
                    ) : null}

                  {(activity.data as IDataItemArray)?.label ? (
                    <div className={styles.contentFile}>
                      <FileUploadIcon className={styles.attachmentIcon} />
                      <span>{(activity.data as IDataItemArray)?.label}</span>
                    </div>
                  ) : null}
                  {Array.isArray(activity.data) && activity.data.length > 0 ? (
                    <div className={styles.contentFiles}>
                      {activity.data.map((item) => (
                        <div
                          key={[item.id, item.label].join('_')}
                          className={styles.contentFile}
                        >
                          <FileUploadIcon className={styles.attachmentIcon} />
                          <span>{item.label}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            {activity.comment ? (
              <div className={styles.contentComment}>
                <span className={styles.titleBox}>
                  {t('taskManagement.commentToChanging')}
                </span>
                <span>
                  {activity.comment}
                </span>
              </div>
            ) : null}
          </div>
        )) : <EmptyList text={emptyListText} />}
      </div>
    </div>
  );
}

export default TaskInfoActivities;
